// Detects a multi-tab session conflict caused by `HybridAuthTokenStorage`'s "remember me" mode
// (see that file): its tokens live in `localStorage` under a fixed key, shared across every tab of
// the same browser origin. Logging in as a SECOND user in a second tab silently overwrites the
// first tab's stored token -- the first tab's own SignalR connection then fails with a cryptic
// "Invocation canceled..." console error the next time it tries to use its (now-stale, revoked)
// token, with no user-visible explanation.
//
// Scope is deliberately narrow, per an explicit product decision: this does NOT attempt to support
// real simultaneous multi-tab sessions, and does NOT change the token storage mechanism (still
// shared via `localStorage` for "remember me" -- that's `HybridAuthTokenStorage`'s call, out of
// scope here). The only job here is turning a silent, confusing failure into a clear, user-visible
// warning in the AFFECTED tab (the one that just lost its session), via the app's existing
// `ToastService` -- the same "global notice" mechanism `dashboard.page.ts` already uses for
// load-error toasts.
//
// Mechanism: the native `storage` event fires ONLY in tabs that did NOT originate a `localStorage`
// write (exactly what's needed -- the tab that just logged in already knows what happened; the
// OTHER tab, silently losing its session, is the one that needs to find out). Compares the new
// token's decoded `sub` claim against the CURRENT tab's own token (`AuthTokenService.tokens()`,
// already in `core/auth/` -- kept dependency-light on purpose, so this stays in `core/` alongside
// the storage it watches, rather than reaching into `features/auth/`'s `AuthStore` the way
// presentation code normally would): a different, non-null subject means another user just logged
// in elsewhere in this browser and overwrote this tab's token underneath it.
//
// A SAME subject with a different `organization_id` claim is the other conflict this watches for:
// a staff account switched clinics in another tab (`web/clinic-selector`'s `uv-user-menu` switcher,
// see `user-menu.component.ts`). That is not a "different user" event -- it is this very tab's own
// session quietly going stale (every other feature store here still holds the OLD clinic's data
// behind a token that now names the NEW one), so instead of a toast it navigates this tab to
// `/elige-clinica?reason=switched-elsewhere`, re-running the exact same real splash/rebuild a
// deliberate switch does (see `clinic-selector.page.ts`'s own reasoning for why an in-place refresh
// of every affected store is not safe).

import { DestroyRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { TRANSLATION_KEYS } from '@shared/constants/translation-keys.const';
// This initializer runs eagerly in both apps; import the neutral service without UI barrels.
import { ToastService } from '@shared/services/toast.service';
import { AUTH_TOKENS_STORAGE_KEY } from '@shared/constants/storage-keys.const';
import { WebRoutes } from '@shared/constants/web-routes.enum';
import { AuthTokenService } from './auth-token.service';
import { decodeJwtOrganizationId, decodeJwtRole, decodeJwtSubject } from './jwt.util';

// Roles that reach another organization through the developer FAB's organization context rather
// than through a `StaffClinicMembership`. Spelled out here rather than imported from
// `@features/shared/auth`'s `UserRole`: `libs/core` cannot depend on a feature (see
// `tools/check-architecture.mjs`).
const PLATFORM_ROLES: ReadonlySet<string> = new Set(['SuperAdmin', 'Developer']);

interface StoredAuthTokens {
  readonly accessToken?: string;
}

@Injectable({ providedIn: 'root' })
export class SessionConflictService {
  private readonly tokenService = inject(AuthTokenService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  // Bound once (not inline in `addEventListener`) so the EXACT SAME function reference can be
  // passed to `removeEventListener` on teardown -- an inline arrow function can never be removed,
  // which would leak one `window`-level listener per instance. Matters most in tests: each spec
  // file's `beforeEach`/`TestBed.inject(SessionConflictService)` creates a fresh instance against
  // the SAME shared jsdom `window` across an entire test run, so without `DestroyRef` cleanup
  // here (which `TestBed.resetTestingModule()` triggers on the previous instance), listeners
  // accumulate indefinitely across every spec file that (re)creates this service, each one still
  // firing (and re-running this handler's own JWT-decode work) on every later test's `storage`
  // event dispatch.
  private readonly boundOnStorageEvent = (event: StorageEvent): void => this.onStorageEvent(event);

  constructor() {
    globalThis.window?.addEventListener('storage', this.boundOnStorageEvent);
    inject(DestroyRef).onDestroy(() => globalThis.window?.removeEventListener('storage', this.boundOnStorageEvent));
  }

  private onStorageEvent(event: StorageEvent): void {
    if (event.key !== AUTH_TOKENS_STORAGE_KEY || !event.newValue) return;

    const currentAccessToken = this.tokenService.tokens()?.accessToken;
    if (!currentAccessToken) return;
    const currentSubject = decodeJwtSubject(currentAccessToken);
    if (!currentSubject) return;

    const newAccessToken = this.accessTokenFrom(event.newValue);
    if (!newAccessToken) return;
    const newSubject = decodeJwtSubject(newAccessToken);
    if (!newSubject) return;

    if (newSubject !== currentSubject) {
      this.toastService.show(this.transloco.translate(TRANSLATION_KEYS.authSessionConflictWarning), 'warning');
      return;
    }

    const currentOrganizationId = decodeJwtOrganizationId(currentAccessToken);
    const newOrganizationId = decodeJwtOrganizationId(newAccessToken);
    if (!newOrganizationId || newOrganizationId === currentOrganizationId) return;

    // `/elige-clinica` rebuilds the session from the account's clinic memberships, of which a
    // platform account has none by design -- it answers 403 for one. So a Developer connecting to
    // another organization from the FAB in one tab must not bounce its other tabs into that dead
    // end; they keep the session they have, which is still valid for the organization they were
    // opened on.
    if (PLATFORM_ROLES.has(decodeJwtRole(currentAccessToken) ?? '')) return;

    void this.router.navigateByUrl(`${WebRoutes.ClinicSelector}?reason=switched-elsewhere`);
  }

  private accessTokenFrom(rawTokens: string): string | null {
    try {
      const tokens = JSON.parse(rawTokens) as StoredAuthTokens;
      return tokens.accessToken ?? null;
    } catch {
      return null;
    }
  }
}
